import * as React from "react";
import styled from "styled-components";
import orderBy from "lodash.orderby";
import { DataGrid, DataGridHeader, DataGridHeading } from "@conserveit/blocks";
import { Direction } from "utils/columns";
import { Zone } from "context/reports/comfort/types";
import { IndexByString } from "redux/modules/insights/utils";
import SortArrow from "../../../../components/SortArrow";
import { SummaryRow } from "./SummaryRow";
import { sortEvent } from "../../../../utils/eventTracking";
import { TRACKING_EVENT_SORT_BY } from "../../../../context/reports/comfort/constants";
import { StyleDataGridHeading } from "./ComfortSummaryDataGrid.styles";

interface Sort {
  sortKey: keyof Zone;
  direction: Direction;
}

interface Props {
  zones: IndexByString<Zone>;
  trackingTitle: string;
}

const StyledArrowWrapper = styled.div<{ active: boolean }>`
  opacity: ${(props) => (props.active ? 1 : 0)};
`;

const GridWrapper = styled.div`
  max-height: 308px;
  overflow: scroll;
`;

export const ComfortSummaryDataGrid = React.memo<Props>(
  ({ zones, trackingTitle }) => {
    const [sort, setSort] = React.useState<Sort>({
      sortKey: "hoursDiscomfort",
      direction: "desc",
    });

    const [zonesArray, setZonesArray] = React.useState<Zone[]>(
      Object.values(zones)
    );

    const handleSortBy = (key: keyof Zone) =>
      React.useCallback(() => {
        const direction = sort.direction === "asc" ? "desc" : "asc";
        sortEvent(
          {
            chartTitle: trackingTitle,
            columnTitle: key,
            direction,
          },
          TRACKING_EVENT_SORT_BY
        );
        setSort({
          sortKey: key,
          direction,
        });
      }, [sort]);

    React.useEffect(() => {
      const orderedZones = orderBy(
        zonesArray,
        (zone: Zone) => zone[sort.sortKey],
        sort.direction
      );

      setZonesArray(orderedZones);
    }, [zones, sort]);

    return (
      <GridWrapper>
        <DataGrid cellProps={{ type: "dense", density: "compact" }}>
          <DataGridHeader cursor="pointer">
            <DataGridHeading onClick={handleSortBy("name")}>
              Zone
              <StyledArrowWrapper active={sort.sortKey === "name"}>
                <SortArrow direction={sort.direction} />
              </StyledArrowWrapper>
            </DataGridHeading>
            <DataGridHeading onClick={handleSortBy("pctDiscomfort")}>
              % of occupancy
              <StyledArrowWrapper active={sort.sortKey === "pctDiscomfort"}>
                <SortArrow direction={sort.direction} />
              </StyledArrowWrapper>
            </DataGridHeading>

            <DataGridHeading onClick={handleSortBy("hoursDiscomfort")}>
              Total (hrs)
              <StyledArrowWrapper active={sort.sortKey === "hoursDiscomfort"}>
                <SortArrow direction={sort.direction} />
              </StyledArrowWrapper>
            </DataGridHeading>

            <DataGridHeading onClick={handleSortBy("hoursHot")}>
              Too hot (hrs)
              <StyledArrowWrapper active={sort.sortKey === "hoursHot"}>
                <SortArrow direction={sort.direction} />
              </StyledArrowWrapper>
            </DataGridHeading>

            <DataGridHeading onClick={handleSortBy("hoursCold")}>
              Too cold (hrs)
              <StyledArrowWrapper active={sort.sortKey === "hoursCold"}>
                <SortArrow direction={sort.direction} />
              </StyledArrowWrapper>
            </DataGridHeading>
            <StyleDataGridHeading>Actions</StyleDataGridHeading>
          </DataGridHeader>

          {zonesArray.map((zone) =>
            zone.pctDiscomfort && zone.pctDiscomfort > 0 ? (
              <SummaryRow key={zone.id} zone={zone} />
            ) : null
          )}
        </DataGrid>
      </GridWrapper>
    );
  },
  (p, n) => p.zones === n.zones
);
